<template>
  <teleport to="#app">
    <div v-if="elementVisible" class="popup-overlay fixed left-0 top-0" @click="hideInfoMessage">
      <div class="absolute overlay-decor" />
      <app-button
        btn-id="hide-pack-opened-message"
        btn-type="primary"
        :btn-text="$t('button.continue')"
        btn-size="md"
        @click="hideInfoMessage"
      />
    </div>

    <transition name="fade">
      <div v-if="elementVisible" class="pack-opened-message-wrapper w-full flex justify-center">
        <div class="pack-opened-message flex flex-col items-center">
          <div class="pack-opened-decor absolute" :class="openedPackMessage.packTypeShorthand"></div>

          <main class="flex flex-col mt-4 items-center">
            <div class="pack-opened-title flexing">
              <span
                v-if="openedPackMessage.amount > 1"
                class="text-32 text-texts-standard-default font-bold mr-4"
                >{{ `${openedPackMessage.amount}x` }}</span
              >

              <p class="text-32 font-bold uppercase" :class="`gradient-text-${openedPackMessage.packTypeShorthand}`">
                {{
                  $replacePlaceholder(
                    $t('gallery.typePackOpened'),
                    '{type}',
                    $t(`gallery.${openedPackMessage.packTypeShorthand}Pack`),
                  )
                }}
              </p>
            </div>

            <p class="text-texts-standard-default text-32 my-6">{{ $t('gallery.youReceived') }}</p>

            <div class="pack-opened-rewards flexing">
              <reward-box
                v-for="(pieceCount, pieceRarity) in openedPackMessage.pieces"
                :key="pieceRarity"
                :reward-icon-size="72"
                :reward-icon-name="`puzzle-${getIconName(pieceRarity)}`"
                :reward-number="pieceCount"
              />
            </div>

            <slot />
          </main>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { GalleryPieceRarity } from '@/interfaces/gallery/enums'
import { useGalleryPacksStore } from '@/store/pinia/gallery/packsStore'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

interface ComponentData {
  elementVisible: boolean
}
export default defineComponent({
  name: 'GalleryPackOpened',

  data(): ComponentData {
    return {
      elementVisible: true,
    }
  },

  computed: {
    ...mapState(useGalleryPacksStore, ['openedPackMessage']),
  },

  methods: {
    ...mapActions(useGalleryPacksStore, ['hideMessage']),

    getIconName(pieceRarity: GalleryPieceRarity): string {
      return (pieceRarity as string).split('_')[2]
    },

    hideInfoMessage(): void {
      this.hideMessage()
      this.elementVisible = false
    }
  }
})
</script>
<style lang="scss" scoped>
@import '@/assets/styles/components/popups.scss';
$pack-types: ('regular', 'jumbo', 'premium');
.pack-opened-message {
  width: 100%;
  min-height: 21.25rem;
  z-index: 5;
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;

    @if $isWsm {
      background: linear-gradient(to right, transparent, #0b1e37, #154e79, #0b1e37, transparent);
      border-image-source: linear-gradient(to right, transparent, #586b9d, #586b9d, transparent);
      border-image-slice: 1;
      border-width: 0.125rem;
    }

    @if $isSsm {
      background: linear-gradient(
        to right,
        transparent,
        rgba(22, 32, 59, 0.97),
        #605e8f,
        #16203b,
        transparent
      );
      border-image-source: linear-gradient(to right, transparent, #586b9d, #586b9d, transparent);
      border-image-slice: 1;
      border-width: 0.125rem;
    }

    mask-image: linear-gradient(to right, transparent, black 20%, black 80%, transparent 100%);
  }

  &-decor {
    width: 70%;
    height: 20rem;
    margin: 0 auto;
    background: url($path-premium + 'afterPayment/BG_EFFECT_4.avif') center bottom no-repeat;
    background-size: cover;
    position: absolute;
    top: -20rem;
  }

  header {
    width: 100%;
    height: 5rem;
    background-image: url($path-components + 'progressMessage/BG_TOP_CONTENT.avif');
    background-size: cover;
    background-repeat: no-repeat;
  }

  main {
    width: 100%;
    min-height: 20rem;
  }
  .pack-opened {
    &-decor {
      width: 121.25rem;
      height: 20.25rem;
      top: -20.2rem;
      left: 50%;
      transform: translateX(-50%);
      @each $type in $pack-types {
        &.#{$type} {
          @include background(
            url('#{$path-gallery}packs/pack-opened-decor-#{$type}.avif'),
            contain
          );
        }
      }
    }
    &-title {
      min-width: 50.5rem;
      height: 3.125rem;
      @if $isWsm {
        background-image: linear-gradient(
          to right,
          transparent,
          rgba(108, 158, 196, 0.5),
          transparent
        );
      }
      @if $isSsm {
        background-image: linear-gradient(
          to right,
          transparent,
          rgba(44, 54, 92, 0.9),
          transparent
        );
      }
    }
    &-reward {
      width: 26rem;
      margin: 0 auto;
    }
  }
  :deep() {
    [class^='icon-puzzle'] {
      position: relative;
      left: 0.2rem;
      top: 0.2rem;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  opacity: 1;
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.overlay-decor {
  @include background(url('#{$path-gallery}packs/gallery-packs-emotion-bg.avif'), contain);
  width: 138.813rem;
  height: 60.375rem;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}
#hide-pack-opened-message {
  position: absolute;
  bottom: 2.5rem;
  right: 2.5rem;
}
.gradient-text-regular {
  background-clip: text;
  text-shadow: none;
  color: transparent;

  @if $isWsm {
    background-image: linear-gradient(to top, #ef7f52, #ffb99e);
  }

  @if $isSsm {
    background-image: linear-gradient(to top, #ef7f52, #ffb99e);
  }
}
.gradient-text-premium {
  background-clip: text;
  text-shadow: none;
  color: transparent;

  @if $isWsm {
    background-image: linear-gradient(to top, #f6c717, #fff284);
  }

  @if $isSsm {
    background-image: linear-gradient(to top, #f6c717, #fff284);
  }
}
.gradient-text-jumbo {
  background-clip: text;
  text-shadow: none;
  color: transparent;

  @if $isWsm {
    background-image: linear-gradient(to top, #c95ddf, #eaaaf9);
  }

  @if $isSsm {
    background-image: linear-gradient(to top, #c95ddf, #eaaaf9);
  }
}
</style>
